/**
 * Wissensquellen (Roadmap V-13) - frueher nur Buecher, daher der Name.
 * Eingaben werden hier geprueft: Titel nicht leer, Links nur http(s),
 * Bewertung 1-10, Fortschritt nicht negativ.
 */
import { db } from '@/db/schema';
import { bewertungGueltig, linkGueltig } from '@/domain/wissen';
import { parseTags } from '@/domain/resources/search';
import { createTask } from '@/db/repositories/tasks';
import { inDenPapierkorb, papierkorbTitel } from '@/db/repositories/papierkorb';
import { addThought, toggleMerken } from '@/db/repositories/thoughts';
import { nowInstant, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { Book, BookStatus, ID, LocalDate } from '@/domain/types';

export type { BookStatus };

export type BookDraft = Pick<Book, 'title'> & Partial<Omit<Book, 'id' | 'createdAt' | 'updatedAt' | 'title'>>;

/** Gemeinsame Pruefung fuer Anlegen und Aendern. */
function bereinigt(patch: Partial<Book>): Partial<Book> {
  const out: Partial<Book> = { ...patch };
  if ('title' in patch) out.title = patch.title?.trim() ?? '';
  if ('author' in patch) out.author = patch.author?.trim() || undefined;
  if ('url' in patch) out.url = linkGueltig(patch.url);
  if ('rating' in patch) out.rating = bewertungGueltig(patch.rating);
  if ('notes' in patch) out.notes = patch.notes?.trim() ? patch.notes : undefined;
  if ('erkenntnisse' in patch) out.erkenntnisse = patch.erkenntnisse?.trim() ? patch.erkenntnisse : undefined;
  if ('tags' in patch) out.tags = parseTags((patch.tags ?? []).join(' '));
  if ('areaIds' in patch) out.areaIds = [...new Set(patch.areaIds ?? [])];
  if ('projectIds' in patch) out.projectIds = [...new Set(patch.projectIds ?? [])];
  if ('goalIds' in patch) out.goalIds = [...new Set(patch.goalIds ?? [])];
  if (patch.fortschritt) {
    const gesamt = Math.max(0, Math.round(patch.fortschritt.gesamt || 0));
    const aktuell = Math.max(0, Math.round(patch.fortschritt.aktuell || 0));
    out.fortschritt = gesamt > 0 ? { gesamt, aktuell: Math.min(aktuell, gesamt) } : undefined;
  }
  return out;
}

export async function createBook(draft: BookDraft): Promise<Book> {
  const ts = nowInstant();
  const { areaId, ...rest } = draft;
  const sauber = bereinigt({ ...rest, tags: rest.tags ?? [], areaIds: rest.areaIds ?? (areaId ? [areaId] : []) });
  const book: Book = {
    ...sauber,
    id: newId(),
    title: draft.title.trim(),
    typ: sauber.typ ?? 'buch',
    status: sauber.status ?? 'wunsch',
    tags: sauber.tags ?? [],
    areaIds: sauber.areaIds ?? [],
    // Bereiche stehen in `areaIds` (V-13), das Einzelfeld bleibt leer.
    areaId: null,
    createdAt: ts,
    updatedAt: ts,
  };
  if (!book.title) throw new Error('Titel fehlt');
  await db.books.add(book);
  return book;
}

export async function updateBook(id: ID, patch: Partial<Omit<Book, 'id' | 'createdAt'>>): Promise<void> {
  const b = bereinigt(patch);
  if ('title' in b && !b.title) delete b.title;
  await db.books.update(id, { ...b, updatedAt: nowInstant() });
}

/**
 * Status wechseln. Begonnen- und Abschlussdatum werden mitgesetzt, sofern
 * noch keines dasteht - sonst muesste man sie nachtragen und liesse es.
 */
export async function setBookStatus(id: ID, status: BookStatus, heute: LocalDate): Promise<void> {
  const buch = await db.books.get(id);
  if (!buch) return;
  const patch: Partial<Book> = { status };
  if (status === 'lese' && !buch.startedAt) patch.startedAt = heute;
  if ((status === 'gelesen' || status === 'abgebrochen') && !buch.finishedAt) patch.finishedAt = heute;
  if (status === 'gelesen' && buch.fortschritt) patch.fortschritt = { ...buch.fortschritt, aktuell: buch.fortschritt.gesamt };
  await updateBook(id, patch);
}

export async function deleteBook(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'books', zeileIds: [id] }, () => db.books.delete(id));
}

// ---------------------------------------------------------------------------
// Notizen, Erkenntnisse, Stellen (F-20)
// ---------------------------------------------------------------------------

/** Stelle oder Zitat anlegen; leerer Text wird ignoriert. */
export async function addStelle(bookId: ID, ort: string, text: string): Promise<void> {
  const t = text.trim();
  if (!t) return;
  await db.books.where('id').equals(bookId).modify((b) => {
    b.stellen = [...(b.stellen ?? []), { id: newId(), ort: ort.trim().slice(0, 40), text: t }];
    b.updatedAt = nowInstant();
  });
}

export async function updateStelle(bookId: ID, stelleId: ID, patch: { ort?: string; text?: string }): Promise<void> {
  await db.books.where('id').equals(bookId).modify((b) => {
    b.stellen = (b.stellen ?? []).map((s) => (s.id !== stelleId ? s : {
      ...s,
      ...(patch.ort !== undefined ? { ort: patch.ort.trim().slice(0, 40) } : {}),
      // Ein leerer Text loescht nicht versehentlich - dafuer gibt es Loeschen.
      ...(patch.text?.trim() ? { text: patch.text.trim() } : {}),
    }));
    b.updatedAt = nowInstant();
  });
}

export async function deleteStelle(bookId: ID, stelleId: ID): Promise<void> {
  const stelle = (await db.books.get(bookId))?.stellen?.find((s) => s.id === stelleId);
  await inDenPapierkorb({ art: 'entfernt', tabelle: 'books', zeileIds: [bookId], titel: papierkorbTitel(stelle?.text) }, () => db.books.where('id').equals(bookId).modify((b) => {
    b.stellen = (b.stellen ?? []).filter((s) => s.id !== stelleId);
    b.updatedAt = nowInstant();
  }));
}

/**
 * Eine Erkenntnis umsetzen: als Aufgabe (erster Bereich des Eintrags, Notiz
 * "Aus: Titel") oder als gemerkter Gedanke mit Rueckverweis. Jede nur einmal
 * je Art - ein zweiter Klick liefert das Bestehende.
 */
export async function erkenntnisUmsetzen(bookId: ID, text: string, art: 'aufgabe' | 'gedanke'): Promise<ID | null> {
  const t = text.trim();
  const b = await db.books.get(bookId);
  if (!b || !t) return null;
  const schon = (b.umgesetzt ?? []).find((u) => u.text === t && u.art === art);
  if (schon) return schon.id;

  let id: ID;
  if (art === 'aufgabe') {
    id = (await createTask({ title: t.slice(0, 200), status: 'naechste', areaId: b.areaIds[0] ?? null, notes: `Aus: ${b.title}` })).id;
  } else {
    const g = await addThought(today(), t, { art: 'wissen', key: b.id, titel: b.title });
    if (!g) return null;
    await toggleMerken(g.id);
    id = g.id;
  }
  await db.books.update(bookId, { umgesetzt: [...(b.umgesetzt ?? []), { text: t, art, id }], updatedAt: nowInstant() });
  return id;
}

// ---------------------------------------------------------------------------
// Verknuepfungen (F-19): Bereiche, Projekte, Ziele - mehrfach, ohne Hierarchie
// ---------------------------------------------------------------------------

export type WissensVerknuepfung = 'bereich' | 'projekt' | 'ziel';

const FELD = { bereich: 'areaIds', projekt: 'projectIds', ziel: 'goalIds' } as const;

/** Verknuepft - nur mit etwas, das es gibt. Doppelt wird nichts. */
export async function wissenVerknuepfen(bookId: ID, art: WissensVerknuepfung, zielId: ID): Promise<boolean> {
  const tabelle = art === 'bereich' ? db.areas : art === 'projekt' ? db.projects : db.goals;
  return db.transaction('rw', db.books, tabelle, async () => {
    if (!(await tabelle.get(zielId))) return false;
    const feld = FELD[art];
    let geaendert = false;
    await db.books.where('id').equals(bookId).modify((b) => {
      const liste = b[feld] ?? [];
      if (liste.includes(zielId)) return;
      b[feld] = [...liste, zielId];
      b.updatedAt = nowInstant();
      geaendert = true;
    });
    return geaendert;
  });
}

export async function wissenLoesen(bookId: ID, art: WissensVerknuepfung, zielId: ID): Promise<void> {
  const feld = FELD[art];
  await db.books.where('id').equals(bookId).modify((b) => {
    b[feld] = (b[feld] ?? []).filter((x) => x !== zielId);
    b.updatedAt = nowInstant();
  });
}
